import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { HoursSummary, DayTracker } from "@/components/TimesheetInsights";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { API, PHASES, STATUSES, fmt } from "@/lib/engagements";

const today = () => new Date().toISOString().slice(0, 10);

const TYPE_LABEL = { client: "Client work", leave: "Leave", holiday: "Public Holiday" };
const TYPE_STYLE = {
  client: "bg-zinc-100 text-zinc-700 border-zinc-200",
  leave: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
  holiday: "bg-[#FAF5FF] text-[#6B21A8] border-[#E9D5FF]",
};

export default function Timesheet() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSupervisor = user?.role === "supervisor";
  const canSeeAll = isAdmin || isSupervisor;
  const canRecordInternal = canSeeAll;
  const [engagements, setEngagements] = useState([]);
  const [entries, setEntries] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [staffFilter, setStaffFilter] = useState("");
  const [listFrom, setListFrom] = useState("");
  const [listTo, setListTo] = useState("");
  const [entryType, setEntryType] = useState(
    user?.role === "admin" || user?.role === "supervisor" ? "leave" : "client"
  );
  const [internalForm, setInternalForm] = useState({ user_id: "", date: today(), hours: "8", remarks: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", engagement_id: "", hours: "", remarks: "" });
  const [form, setForm] = useState({
    date: today(), engagement_id: "", phase: "account", milestone: "", hours: "", status: "", remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    const [eng, ts] = await Promise.all([
      axios.get(`${API}/engagements`),
      axios.get(`${API}/timesheets`, {
        params: { staff: staffFilter, from_date: listFrom, to_date: listTo },
      }),
    ]);
    setEngagements(eng.data);
    setEntries(ts.data);
    if (canSeeAll) {
      const s = await axios.get(`${API}/auth/staff`);
      setStaffList(s.data);
    }
  }, [staffFilter, listFrom, listTo, canSeeAll]);

  useEffect(() => {
    if (canSeeAll) setEntryType("leave");
  }, [canSeeAll]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => engagements.find((e) => e.id === form.engagement_id),
    [engagements, form.engagement_id]
  );
  const availablePhases = useMemo(
    () => PHASES.filter((p) => !selected || selected.services?.[p.key] !== false),
    [selected]
  );
  const milestones = PHASES.find((p) => p.key === form.phase)?.fields || [];
  const staffNames = useMemo(
    () => Array.from(new Set([...staffList.filter((s) => s.role === "staff").map((s) => s.name),
                              ...entries.map((e) => e.staff_name)])).filter(Boolean).sort(),
    [staffList, entries]
  );
  const totalHours = entries.reduce((a, e) => a + Number(e.hours || 0), 0);

  const grouped = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const key = e.staff_name || "Unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [entries]);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.engagement_id) return toast.error("Choose a client engagement");
    if (!form.hours) return toast.error("Enter hours worked");
    setBusy(true);
    try {
      await axios.post(`${API}/timesheets`, {
        date: form.date,
        engagement_id: form.engagement_id,
        phase: form.phase,
        milestone: form.milestone || null,
        hours: Number(form.hours),
        status: form.status || null,
        remarks: form.remarks || null,
      });
      toast.success("Timesheet saved — engagement updated");
      setForm({ ...form, milestone: "", hours: "", status: "", remarks: "" });
      load();
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/timesheets/${id}`);
      toast.success("Entry deleted");
      load();
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitInternal = async (ev) => {
    ev.preventDefault();
    if (!internalForm.user_id) return toast.error("Choose a staff member");
    setBusy(true);
    try {
      await axios.post(`${API}/timesheets/internal`, {
        user_id: internalForm.user_id,
        date: internalForm.date,
        entry_type: entryType,
        hours: Number(internalForm.hours || 8),
        remarks: internalForm.remarks || null,
      });
      toast.success(`${TYPE_LABEL[entryType]} recorded`);
      setInternalForm({ ...internalForm, remarks: "" });
      load();
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const canModify = (e) => {
    const internal = (e.entry_type || "client") !== "client";
    if (internal) return canSeeAll;
    if (isSupervisor) return false;
    return isAdmin || e.user_id === user?.id;
  };

  const openEdit = (e) => {
    setEditing(e);
    setEditForm({
      date: e.date || "",
      engagement_id: e.engagement_id || "",
      hours: String(e.hours ?? ""),
      remarks: e.remarks || "",
    });
  };

  const saveEdit = async () => {
    try {
      const payload = {
        date: editForm.date,
        hours: Number(editForm.hours),
        remarks: editForm.remarks,
      };
      if ((editing.entry_type || "client") === "client" && editForm.engagement_id) {
        payload.engagement_id = editForm.engagement_id;
      }
      await axios.put(`${API}/timesheets/${editing.id}`, payload);
      toast.success("Entry updated");
      setEditing(null);
      load();
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-3xl font-extrabold">Timesheet</h2>
        <p className="mt-1 text-sm text-zinc-500">
          {canSeeAll
            ? "Timesheet entry is for staff accounts only — you can record leave and public holidays, and review everyone's entries below."
            : "Log your hours against a client task — the milestone date and status update automatically. You can edit your own entries later."}
        </p>
      </div>

      {canRecordInternal && (
        <div className="flex flex-wrap items-center gap-2 border border-border bg-white p-3">
          <span className="mr-1 text-[10px] uppercase tracking-widest text-zinc-500">Record</span>
          {["leave", "holiday"].map((t) => (
            <button
              key={t}
              data-testid={`entry-type-${t}`}
              onClick={() => setEntryType(t)}
              className={`rounded-sm border px-3 py-1.5 text-sm font-medium transition-colors ${
                entryType === t ? "border-zinc-900 bg-zinc-900 text-white" : "border-border hover:bg-zinc-50"
              }`}
            >
              {TYPE_LABEL[t]}
            </button>
          ))}
          <span className="ml-auto text-xs text-zinc-500">
            Counts toward the 8-hour day, not charged to any client.
          </span>
        </div>
      )}

      {entryType !== "client" && canRecordInternal && (        <form
          data-testid="internal-form"
          onSubmit={submitInternal}
          className="grid grid-cols-1 gap-4 border border-border bg-white p-5 md:grid-cols-5"
        >
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Staff member</Label>
            <select
              data-testid="internal-staff"
              value={internalForm.user_id}
              onChange={(e) => setInternalForm({ ...internalForm, user_id: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm"
            >
              <option value="">Select staff…</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.staff_id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Date</Label>
            <Input data-testid="internal-date" type="date" value={internalForm.date}
                   onChange={(e) => setInternalForm({ ...internalForm, date: e.target.value })}
                   className="mt-1.5 rounded-sm tabular" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Hours</Label>
            <Input data-testid="internal-hours" type="number" step="0.25" min="0.25" value={internalForm.hours}
                   onChange={(e) => setInternalForm({ ...internalForm, hours: e.target.value })}
                   className="mt-1.5 rounded-sm tabular" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Remarks</Label>
            <Input data-testid="internal-remarks" value={internalForm.remarks}
                   onChange={(e) => setInternalForm({ ...internalForm, remarks: e.target.value })}
                   placeholder={entryType === "leave" ? "e.g. annual leave" : "e.g. Hari Raya"}
                   className="mt-1.5 rounded-sm" />
          </div>
          <div className="flex items-end">
            <Button data-testid="internal-submit" type="submit" disabled={busy}
                    className="w-full rounded-sm bg-black text-white hover:bg-zinc-800 hover:text-white">
              {busy ? "Saving…" : `Record ${TYPE_LABEL[entryType]}`}
            </Button>
          </div>
        </form>
      )}

      <form
        data-testid="timesheet-form"
        onSubmit={submit}
        className={`grid grid-cols-1 gap-4 border border-border bg-white p-5 md:grid-cols-3 ${
          canSeeAll ? "hidden" : ""
        }`}
      >
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Date</Label>
          <Input
            data-testid="ts-date"
            type="date"
            min={user?.start_date || undefined}
            max={user?.end_date || undefined}
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="mt-1.5 rounded-sm tabular"
          />
          {(user?.start_date || user?.end_date) && (
            <p className="mt-1 text-[11px] text-zinc-500">
              You can log {user?.start_date ? `from ${user.start_date}` : "any date"}
              {user?.end_date ? ` to ${user.end_date}` : ""}
            </p>
          )}
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Client engagement</Label>
          <select
            data-testid="ts-engagement"
            value={form.engagement_id}
            onChange={(e) => setForm({ ...form, engagement_id: e.target.value, phase: "account", milestone: "" })}
            className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm"
          >
            <option value="">Select client…</option>
            {engagements.map((e) => (
              <option key={e.id} value={e.id}>
                {e.client} — {fmt(e.year_end) || "no year end"} ({e.status})
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Task</Label>
          <select
            data-testid="ts-phase"
            value={form.phase}
            onChange={(e) => setForm({ ...form, phase: e.target.value, milestone: "" })}
            className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm"
          >
            {availablePhases.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Milestone completed</Label>
          <select
            data-testid="ts-milestone"
            value={form.milestone}
            onChange={(e) => setForm({ ...form, milestone: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm"
          >
            <option value="">None — just logging hours</option>
            {milestones.map(([f, label]) => (
              <option key={f} value={f}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Hours</Label>
          <Input
            data-testid="ts-hours"
            type="number"
            step="0.25"
            min="0"
            value={form.hours}
            onChange={(e) => setForm({ ...form, hours: e.target.value })}
            className="mt-1.5 rounded-sm tabular"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Set status</Label>
          <select
            data-testid="ts-status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm"
          >
            <option value="">Leave as is</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Remarks</Label>
          <Textarea
            data-testid="ts-remarks"
            rows={2}
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            className="mt-1.5 rounded-sm"
          />
        </div>
        <div className="flex items-end">
          <Button
            data-testid="ts-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-sm bg-black text-white hover:bg-zinc-800 hover:text-white"
          >
            {busy ? "Saving…" : "Save entry"}
          </Button>
        </div>
      </form>

      <div className="flex flex-wrap items-end gap-3">
        {canSeeAll && (
          <>
            <select
              data-testid="ts-staff-filter"
              value={staffFilter}
              onChange={(e) => setStaffFilter(e.target.value)}
              className="h-10 rounded-sm border border-input bg-white px-3 text-sm"
            >
              <option value="">All staff</option>
              {staffNames.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">From</Label>
              <Input data-testid="ts-list-from" type="date" value={listFrom}
                     onChange={(e) => setListFrom(e.target.value)}
                     className="mt-1 h-10 rounded-sm tabular" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">To</Label>
              <Input data-testid="ts-list-to" type="date" value={listTo}
                     onChange={(e) => setListTo(e.target.value)}
                     className="mt-1 h-10 rounded-sm tabular" />
            </div>
            {(listFrom || listTo || staffFilter) && (
              <Button data-testid="ts-clear-filters" variant="outline" className="h-10 rounded-sm"
                      onClick={() => { setListFrom(""); setListTo(""); setStaffFilter(""); }}>
                Clear
              </Button>
            )}
          </>
        )}
        <span data-testid="ts-total-hours" className="text-xs uppercase tracking-widest text-zinc-500">
          {entries.length} {entries.length === 1 ? "entry" : "entries"} · {totalHours.toFixed(2)} hours
        </span>
      </div>

      {grouped.length === 0 && (
        <div data-testid="timesheet-empty" className="border border-border bg-white p-10 text-center text-sm text-zinc-500">
          No timesheet entries for this selection.
        </div>
      )}

      {grouped.map(([staffName, rows]) => (
        <div key={staffName} data-testid={`ts-group-${staffName}`} className="border border-border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-zinc-50 px-4 py-2.5">
            <span className="font-display text-base font-extrabold">{staffName}</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              {rows.length} {rows.length === 1 ? "entry" : "entries"} ·{" "}
              {rows.reduce((a, r) => a + Number(r.hours || 0), 0).toFixed(2)} hours
            </span>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="timesheet-table" className="w-full text-left text-xs">
              <thead className="bg-zinc-900 text-[10px] uppercase tracking-widest text-white">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Task</th>
                  <th className="px-3 py-2">Milestone</th>
                  <th className="px-3 py-2 text-right">Hours</th>
                  <th className="px-3 py-2">Status set</th>
                  <th className="px-3 py-2">Remarks</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const type = e.entry_type || "client";
                  return (
                    <tr key={e.id} data-testid={`ts-row-${e.id}`} className="border-t border-border hover:bg-zinc-50">
                      <td className="px-3 py-2 tabular">{fmt(e.date)}</td>
                      <td className="px-3 py-2">
                        <span data-testid={`ts-type-${e.id}`}
                              className={`inline-flex whitespace-nowrap rounded-sm border px-2 py-0.5 text-[11px] font-medium ${TYPE_STYLE[type]}`}>
                          {TYPE_LABEL[type]}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-semibold">{e.client || "—"}</td>
                      <td className="px-3 py-2 capitalize">{e.phase || "—"}</td>
                      <td className="px-3 py-2 text-zinc-600">
                        {(PHASES.find((p) => p.key === e.phase)?.fields || []).find(([f]) => f === e.milestone)?.[1] || "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular">{Number(e.hours).toFixed(2)}</td>
                      <td className="px-3 py-2">{e.status ? <StatusBadge status={e.status} /> : "—"}</td>
                      <td className="px-3 py-2 text-zinc-600">{e.remarks || "—"}</td>
                      <td className="px-3 py-2">
                        {canModify(e) && (
                          <span className="flex items-center justify-end gap-3">
                            <button
                              data-testid={`ts-edit-${e.id}`}
                              onClick={() => openEdit(e)}
                              className="text-zinc-400 transition-colors hover:text-zinc-900"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`ts-delete-${e.id}`}
                              onClick={() => remove(e.id)}
                              className="text-zinc-400 transition-colors hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="ts-edit-dialog" className="bg-white rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">Edit entry</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {(editing?.entry_type || "client") === "client"
                ? "Update the date, client, hours or notes on this entry."
                : `Update this ${TYPE_LABEL[editing?.entry_type || "leave"].toLowerCase()} entry.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Date</Label>
              <Input data-testid="ts-edit-date" type="date" value={editForm.date}
                     onChange={(ev) => setEditForm({ ...editForm, date: ev.target.value })}
                     className="mt-1.5 rounded-sm tabular" />
            </div>
            {(editing?.entry_type || "client") === "client" && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Client engagement</Label>
                <select data-testid="ts-edit-engagement" value={editForm.engagement_id}
                        onChange={(ev) => setEditForm({ ...editForm, engagement_id: ev.target.value })}
                        className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm">
                  {engagements
                    .filter((en) => en.services?.[editing?.phase] !== false)
                    .map((en) => (
                      <option key={en.id} value={en.id}>
                        {en.client} — {fmt(en.year_end) || "no year end"}
                      </option>
                    ))}
                </select>
                <p className="mt-1 text-[11px] text-zinc-500">
                  Only clients where {editing?.phase} work applies are listed.
                </p>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Hours</Label>
              <Input data-testid="ts-edit-hours" type="number" step="0.25" min="0.25" value={editForm.hours}
                     onChange={(ev) => setEditForm({ ...editForm, hours: ev.target.value })}
                     className="mt-1.5 rounded-sm tabular" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Notes</Label>
              <Textarea data-testid="ts-edit-remarks" rows={2} value={editForm.remarks}
                        onChange={(ev) => setEditForm({ ...editForm, remarks: ev.target.value })}
                        className="mt-1.5 rounded-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button data-testid="ts-edit-save" onClick={saveEdit}
                    className="rounded-sm bg-black text-white hover:bg-zinc-800 hover:text-white">
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <HoursSummary canSeeAll={canSeeAll} reloadKey={reloadKey} />
      <DayTracker canSeeAll={canSeeAll} reloadKey={reloadKey} />
    </div>
  );
}
